/**
 * Contatos — quem já falou com este cliente.
 *
 * ## A importação existe, e ela NÃO é uma lista de disparo
 *
 * A versão anterior desta tela dizia "não há importação", e a razão estava certa: em Telegram, Instagram e
 * Facebook o endereço só passa a existir depois de a pessoa escrever, e um botão de importar prometeria
 * **disparo em massa** — que o produto recusa no domínio, e a recusa apareceria depois do upload.
 *
 * O que passou a existir é a outra metade, que é real e que quem troca de ferramenta pede: **trazer nomes e
 * etiquetas**, para que a conversa já comece com o nome certo quando a pessoa escrever. Sem isso, migrar de
 * ferramenta significa atender "+55 27 9…" por semanas.
 *
 * A tela diz as duas coisas: o que a importação faz, e o que ela **não** dá — janela para enviar.
 *
 * **Não tem busca por conteúdo.** §5.1 da spec 05: buscar conteúdo é *ler* conteúdo, e entra na trilha
 * de acesso igual (H18). Nome e número não dependem de decisão nenhuma.
 *
 * ## O bloqueio aparece com o motivo
 *
 * E os motivos não são equivalentes: `opt_out` só o próprio contato desfaz — em SMS é obrigação legal.
 * Mostrar "bloqueado" sem o motivo faria alguém tentar desbloquear o que não se desbloqueia.
 */

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar, Botao, EstadoDaFalha, Etiqueta, falhaDe, identificadorLegivel, urlDaFotoDoContato, useSessao, type FalhaDaApi } from "@hugi/ui";
import { Dica, Gaveta, ListView, PaginaInteira, Selo, type ColunaDaLista } from "@hugi/ui/kit";
import { Contact, Search, Upload } from "lucide-react";
import {
  bloquearContato,
  conferirImportacao,
  desbloquearContato,
  importarContatos,
  registrarOptin,
  useCatalogos,
  useContatos,
  type ConferenciaDaImportacao,
  type ContatoNaLista,
} from "./dados.js";
import "./contatos.css";

/** Motivo de bloqueio → o que a tela diz, e se dá para desfazer. */
const BLOQUEIOS: Readonly<Record<string, { rotulo: string; explicacao: string }>> = {
  opt_out: {
    rotulo: "Pediu para não receber",
    explicacao:
      "Só o próprio contato desfaz, escrevendo de novo. Em SMS isso é obrigação legal, e não há como liberar pelo painel.",
  },
  reclamacao_spam: {
    rotulo: "Marcou como spam",
    explicacao: "Continuar enviando derruba a reputação do seu domínio, inclusive nos e-mails que não saem daqui.",
  },
  manual: { rotulo: "Bloqueado pela equipe", explicacao: "Alguém da sua equipe bloqueou este contato." },
  bot_bloqueado: {
    rotulo: "Bloqueou o bot",
    explicacao: "Não há como alcançar quem bloqueou — ele precisa desbloquear e escrever de novo.",
  },
  identificador_invalido: {
    rotulo: "Endereço inexistente",
    explicacao: "A última tentativa voltou como devolução definitiva. Corrija o dado antes de tentar de novo.",
  },
};

const ORIGEM_DO_OPTIN: Readonly<Record<string, string>> = {
  escreveu_para_nos: "escreveu para você",
  declarado_pelo_cliente: "registrado pela equipe",
  importado_com_prova: "veio na importação, com prova",
  formulario: "preencheu um formulário",
};

/** O identificador cru só é NOME quando não há nome — e aí passa pela máscara. */
const nomeDo = (c: ContatoNaLista) => c.nome ?? c.nomeDoPerfil ?? identificadorLegivel(c.identificador);
const dia = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

/*
  CONSULTA (spec 22): a `ListView` do kit é a tela. A busca e a etiqueta vão AO SERVIDOR (`useContatos`), como antes
  — por isso são ferramentas da lista, e não a busca local do kit, que filtraria de novo o que o servidor já recortou.
  O cartão de cada contato virou a linha; o que não cabe na linha (o motivo do bloqueio, o opt-in com a origem, as
  ações) mora na gaveta do contato.

  O botão "Importar" voltou (31/10): a importação existia, a frase da tela a anunciava, e o botão que a abria tinha
  sumido no `8deb56a` (03/09), quando o cabeçalho trocou de forma — `setImportando` ficou sem chamador.
*/
export function Contatos() {
  const [busca, setBusca] = useState("");
  const [etiquetaId, setEtiquetaId] = useState<string | null>(null);
  const [importando, setImportando] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const sessao = useSessao();
  const catalogos = useCatalogos();
  const contatos = useContatos(busca, etiquetaId);
  const podeGerenciar = sessao.permissoes.includes("manage_contact");
  const filtrando = Boolean(busca || etiquetaId);
  const falha = contatos.error ? falhaDe(contatos.error) : null;
  const contato = aberto ? (contatos.data?.itens.find((c) => c.id === aberto) ?? null) : null;

  const colunas: ColunaDaLista<ContatoNaLista>[] = [
    {
      id: "nome", rotulo: "Contato", principal: true, link: true,
      ordenar: (c) => nomeDo(c).toLocaleLowerCase("pt-BR"),
      dica: (c) => nomeDo(c),
      celula: (c) => (
        <span className="hugi-contatos-nome">
          <Avatar nome={nomeDo(c)} chave={c.identificador} tamanho="pequeno" foto={urlDaFotoDoContato(c.id)} />
          <span className="hugi-contatos-corta">{nomeDo(c)}</span>
        </span>
      ),
    },
    {
      // O identificador é o que se dita ao telefone e o que se procura. Não é PII escondida de quem já tem
      // `read_conversation` — essa pessoa lê a conversa inteira.
      id: "identificador", rotulo: "Endereço",
      celula: (c) => <span className="hugi-contatos-mono">{identificadorLegivel(c.identificador)}</span>,
    },
    {
      id: "situacao", rotulo: "Situação", quebra: true,
      dica: (c) => (c.bloqueio ? BLOQUEIOS[c.bloqueio]?.explicacao : undefined),
      celula: (c) => {
        const bloqueio = c.bloqueio ? BLOQUEIOS[c.bloqueio] : null;
        if (!bloqueio && c.abertos === 0) return "—";
        return (
          <span className="hugi-contatos-selos">
            {c.abertos > 0 ? <Selo tom="sucesso">{`${c.abertos} em aberto`}</Selo> : null}
            {bloqueio ? <Selo tom="perigo">{bloqueio.rotulo}</Selo> : null}
          </span>
        );
      },
    },
    {
      id: "etiquetas", rotulo: "Etiquetas",
      dica: (c) => c.etiquetas.map((e) => e.nome).join(", ") || undefined,
      // Duas na linha e a contagem do resto; a lista inteira no `title` e na gaveta do contato.
      celula: (c) => c.etiquetas.length === 0 ? "—" : (
        <span className="hugi-contatos-selos">
          {c.etiquetas.slice(0, 2).map((e) => <Etiqueta key={e.id} nome={e.nome} cor={e.cor} className="px-2 py-0.5 text-[11px]" />)}
          {c.etiquetas.length > 2 ? <span className="hugi-contatos-fraco">+{c.etiquetas.length - 2}</span> : null}
        </span>
      ),
    },
    {
      // O opt-in é o que decide se uma CAMPANHA alcança este contato (spec 17).
      id: "optin", rotulo: "Opt-in",
      ordenar: (c) => c.optinEm,
      dica: (c) => (c.optinEm ? undefined : "Sem opt-in registrado — campanha não alcança este contato."),
      celula: (c) => (c.optinEm ? dia(c.optinEm) : <span className="hugi-contatos-fraco">sem opt-in</span>),
    },
    { id: "conversas", rotulo: "Conversas", numerica: true, ordenar: (c) => c.atendimentos, celula: (c) => c.atendimentos },
    {
      id: "ultimo", rotulo: "Último contato", ordenar: (c) => c.ultimoContatoEm,
      celula: (c) => (c.ultimoContatoEm ? new Date(c.ultimoContatoEm).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—"),
    },
  ];

  return (
    <PaginaInteira rotulo="Contatos">
      {/* A falha que não se resolve tentando de novo (versão nova, produto não contratado) segue com o estado do Hugi. */}
      {falha && (falha.tipo === "versao_incompativel" || falha.tipo === "sem_entitlement") ? (
        <EstadoDaFalha falha={falha} />
      ) : <ListView
        titulo="Contatos"
        icone={<Contact size={18} />}
        colunas={colunas}
        linhas={contatos.data?.itens as ContatoNaLista[] | undefined}
        chave={(c) => c.id}
        memoria="hugi:contatos"
        celular="cartoes"
        classe="hugi-contatos"
        contagem={(n) => (n === 1 ? "1 contato" : `${n} contatos`)}
        estado={{
          carregando: contatos.isPending,
          erro: Boolean(contatos.error) && falha?.tipo !== "sem_permissao",
          ...(falha?.erro.mensagem ? { mensagemDeErro: falha.erro.mensagem } : {}),
          semPermissao: falha?.tipo === "sem_permissao",
          aoTentarDeNovo: () => void contatos.refetch(),
        }}
        selecao={{ selecionada: aberto, aoSelecionar: (c) => setAberto(c.id) }}
        vazio={{
          semNada: filtrando
            ? "Nenhum contato com esse filtro. Limpe a busca e o filtro de etiqueta para ver a lista inteira."
            : "Nenhum contato ainda. Cada pessoa que escreve para um número conectado passa a aparecer aqui.",
        }}
        acoes={
          <>
            {/* "Onde importo minha lista?" é a primeira pergunta de quem abre — e a resposta cabe numa frase. */}
            <Dica texto="Quem já escreveu para você. Nomes e etiquetas da sua ferramenta antiga entram por planilha." />
            <label className="v2-lista-busca">
              <Search size={14} aria-hidden="true" />
              <input
                type="search"
                aria-label="Buscar por nome ou número"
                placeholder="Nome ou número"
                title="Buscar por nome ou número"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </label>
            <select
              aria-label="Filtrar por etiqueta"
              value={etiquetaId ?? ""}
              onChange={(e) => setEtiquetaId(e.target.value || null)}
              className="hugi-contatos-filtro"
            >
              <option value="">Todas as etiquetas</option>
              {(catalogos.data?.etiquetas ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nome}
                </option>
              ))}
            </select>
            {podeGerenciar ? (
              <button type="button" className="v2-botao" onClick={() => setImportando(true)}>
                <Upload size={14} aria-hidden="true" />
                Importar
              </button>
            ) : null}
          </>
        }
      />}

      <Gaveta aberta={contato !== null} aoFechar={() => setAberto(null)} titulo={contato ? nomeDo(contato) : "Contato"}>
        {contato ? <FichaDoContato contato={contato} podeGerenciar={podeGerenciar} /> : null}
      </Gaveta>

      <Gaveta aberta={importando} aoFechar={() => setImportando(false)} titulo="Importar de uma planilha" largura="larga">
        <Importar />
      </Gaveta>
    </PaginaInteira>
  );
}

/** O contato inteiro — o que a linha não cabe: o motivo do bloqueio, o opt-in com a origem, as ações. */
function FichaDoContato(props: { readonly contato: ContatoNaLista; readonly podeGerenciar: boolean }) {
  const { contato: c, podeGerenciar } = props;
  const nome = nomeDo(c);
  const bloqueio = c.bloqueio ? BLOQUEIOS[c.bloqueio] : null;
  const cliente = useQueryClient();
  const [ocupado, setOcupado] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);

  const agir = async (fn: () => Promise<{ ok: boolean; falha?: FalhaDaApi }>) => {
    setOcupado(true);
    setRecado(null);
    const r = await fn();
    setOcupado(false);
    const f = r.falha;
    const frase = f && "erro" in f && f.erro && typeof f.erro === "object" && "mensagem" in f.erro ? String((f.erro as { mensagem?: unknown }).mensagem ?? "") : "";
    if (!r.ok) setRecado(frase || "Não deu certo. Tente de novo.");
  };

  return (
    <article className="hugi-contatos-ficha">
      <header className="hugi-contatos-ficha-topo">
        <Avatar
          nome={nome}
          chave={c.identificador}
          canal={c.canal === "whatsapp" ? "WA" : c.canal.slice(0, 2).toUpperCase()}
          foto={urlDaFotoDoContato(c.id)}
        />
        <div className="hugi-contatos-ficha-quem">
          {/* O handle cru, em mono, para quem precisa ditar ou procurar. */}
          <p className="hugi-contatos-mono">{identificadorLegivel(c.identificador)}</p>
          <p className="hugi-contatos-selos">
            {c.abertos > 0 ? <Selo tom="sucesso">{`${c.abertos} em aberto`}</Selo> : null}
            {bloqueio ? <Selo tom="perigo">{bloqueio.rotulo}</Selo> : null}
            {c.etiquetas.map((e) => (
              <Etiqueta key={e.id} nome={e.nome} cor={e.cor} className="px-2 py-0.5 text-[11px]" />
            ))}
          </p>
        </div>
      </header>

      {/* O bloqueio aparece com o motivo: `opt_out` só o próprio contato desfaz. */}
      {bloqueio ? <p className="hugi-contatos-erro">{bloqueio.explicacao}</p> : null}

      <dl className="hugi-contatos-campos">
        <dt>Opt-in</dt>
        <dd>
          {c.optinEm
            ? `${ORIGEM_DO_OPTIN[c.optinOrigem ?? ""] ?? c.optinOrigem ?? "registrado"} em ${dia(c.optinEm)}`
            : "Sem opt-in registrado — campanha não alcança este contato."}
        </dd>
        <dt>Conversas</dt>
        <dd>{c.atendimentos}</dd>
        <dt>Primeiro contato</dt>
        <dd>{dia(c.primeiroContatoEm)}</dd>
        <dt>Último</dt>
        <dd>{c.ultimoContatoEm ? new Date(c.ultimoContatoEm).toLocaleString("pt-BR") : "—"}</dd>
      </dl>

      {podeGerenciar ? (
        <div className="hugi-contatos-acoes">
          {!c.optinEm ? (
            <Botao
              tipo="secundario"
              desabilitado={ocupado}
              onClick={() => {
                const nota = window.prompt("Onde e quando esta pessoa pediu para receber? (fica registrado como prova)");
                if (nota === null) return;
                void agir(() => registrarOptin(cliente, c.id, nota.trim() || null));
              }}
            >
              Registrar opt-in
            </Botao>
          ) : null}
          {!c.bloqueio ? (
            <Botao tipo="perigo" desabilitado={ocupado} onClick={() => void agir(() => bloquearContato(cliente, c.id, "manual"))}>
              Bloquear
            </Botao>
          ) : c.bloqueio === "manual" ? (
            <Botao tipo="secundario" desabilitado={ocupado} onClick={() => void agir(() => desbloquearContato(cliente, c.id))}>
              Desbloquear
            </Botao>
          ) : null}
        </div>
      ) : null}
      {recado ? <p className="hugi-contatos-erro" role="alert">{recado}</p> : null}
    </article>
  );
}

/**
 * Importar — colar a planilha, CONFERIR, e só então gravar.
 *
 * ## A conferência não é enfeite
 *
 * É a operação em que o erro é mais caro e mais silencioso do produto: uma coluna trocada grava 3.000 nomes
 * no campo de telefone, e ninguém percebe até um atendente abrir uma conversa. Ver as dez primeiras linhas
 * **interpretadas** — e a lista do que foi recusado, com o número da linha — transforma isso num problema de
 * planilha em vez de num problema de banco.
 *
 * ## E o aviso que vem antes de tudo
 *
 * Importar **não** dá permissão de enviar. Contato importado não tem janela aberta, e no WhatsApp isso
 * significa que só template alcança. Dizer isso depois do upload seria dizer tarde.
 */
function Importar() {
  const cliente = useQueryClient();
  const [csv, setCsv] = useState("");
  const [conferido, setConferido] = useState<ConferenciaDaImportacao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [comProva, setComProva] = useState(false);

  const conferir = async () => {
    setOcupado(true);
    setErro(null);
    setResultado(null);
    try {
      setConferido(await conferirImportacao(csv, "whatsapp"));
    } catch {
      setErro("Não foi possível conferir a planilha.");
    }
    setOcupado(false);
  };

  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await importarContatos(cliente, csv, "whatsapp", comProva);
    setOcupado(false);
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível importar.");
      return;
    }
    setResultado(
      `${r.criados} contato(s) criado(s), ${r.atualizados} já existia(m), ` +
        `${r.etiquetasCriadas} etiqueta(s) nova(s)` +
        (r.recusadas.length > 0 ? `, ${r.recusadas.length} linha(s) recusada(s)` : ""),
    );
    setConferido(null);
    setCsv("");
  };

  return (
    <div className="hugi-contatos-importar">
      <p className="text-xs text-[color:var(--tinta-fraca)]">
        Uma linha por contato: <strong>telefone com código do país</strong>, nome, e etiquetas separadas por{" "}
        <code>|</code>. Cabeçalho é opcional, e o separador pode ser <code>;</code> ou <code>,</code>.
      </p>

      {/*
        O aviso vem ANTES do campo, e não depois do resultado: é a diferença entre a pessoa entender o que
        está fazendo e descobrir depois que a lista não serve para o que ela queria.
      */}
      <p className="text-xs text-[color:var(--atencao-texto)]">
        Importar traz <strong>nome e etiqueta</strong> — não abre permissão para enviar mensagem. No WhatsApp,
        só dá para escrever primeiro por template aprovado, e isso vale igual para contato importado e para
        quem não escreve há dois dias.
      </p>

      <textarea
        className="campo min-h-32 font-mono text-xs"
        value={csv}
        onChange={(e) => {
          setCsv(e.target.value);
          setConferido(null);
        }}
        placeholder={"5527999998888;Maria Silva;VIP|Convênio\n5511988887777;João Souza;Novo"}
      />

      {/*
        O opt-in importado é o CLIENTE assinando que o consentimento existe (spec 17 §7). Desmarcado por
        padrão — caixa marcada de fábrica não registra decisão, registra que ninguém a desmarcou.
      */}
      <label className="flex items-start gap-2 text-xs">
        <input type="checkbox" checked={comProva} onChange={(e) => setComProva(e.target.checked)} />
        <span>
          Tenho o registro de que estas pessoas <strong>pediram para receber</strong> mensagens minhas. Marca cada
          contato com opt-in (origem: importado com prova). Sem isto, campanha não os alcança.
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <Botao
          tipo="secundario"
          onClick={() => void conferir()}
          desabilitado={ocupado || csv.trim() === ""}
          {...(csv.trim() === "" ? { motivoDeDesabilitar: "Cole a planilha primeiro." } : {})}
        >
          Conferir
        </Botao>
        <Botao
          onClick={() => void gravar()}
          desabilitado={ocupado || conferido === null || conferido.quantasAceitas === 0}
          {...(conferido === null
            ? { motivoDeDesabilitar: "Confira antes de importar." }
            : conferido.quantasAceitas === 0
              ? { motivoDeDesabilitar: "Nenhuma linha da planilha serve." }
              : {})}
        >
          Importar {conferido ? `${conferido.quantasAceitas} contato(s)` : ""}
        </Botao>
      </div>

      {erro && <p className="text-sm text-[color:var(--erro-texto)]">{erro}</p>}
      {resultado && <p className="text-sm text-[color:var(--tinta-fraca)]">{resultado}</p>}

      {conferido && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium">
            Como as colunas foram lidas ({conferido.quantasAceitas} linha(s) servem)
          </p>
          <table className="text-xs">
            <thead>
              <tr className="text-left text-[color:var(--tinta-fraca)]">
                <th className="pr-3">Endereço</th>
                <th className="pr-3">Nome</th>
                <th>Etiquetas</th>
              </tr>
            </thead>
            <tbody>
              {conferido.amostra.map((a) => (
                <tr key={a.linha}>
                  <td className="pr-3 font-mono">{a.identificador}</td>
                  <td className="pr-3">{a.nome ?? "—"}</td>
                  <td>{a.etiquetas.join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {conferido.etiquetas.length > 0 && (
            <p className="text-xs text-[color:var(--tinta-fraca)]">
              Etiquetas que serão criadas se não existirem: {conferido.etiquetas.join(", ")}
            </p>
          )}

          {conferido.quantasRecusadas > 0 && (
            <div>
              <p className="text-xs font-medium text-[color:var(--atencao-texto)]">
                {conferido.quantasRecusadas} linha(s) não entram
              </p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {conferido.recusadas.map((r) => (
                  // A LINHA é o que torna isto acionável: "3 linhas com problema" é inútil numa planilha
                  // de 2.000; "linha 417" a pessoa corrige em dez segundos.
                  <li key={r.linha} className="text-xs text-[color:var(--tinta-fraca)]">
                    <strong>Linha {r.linha}:</strong> {r.explicacao}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
